import { useCallback, useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import api, { formatApiError, fileUrl } from "@/lib/api";
import { PageHeader, Surface, Badge, Button, Modal, Input, Select, NewButton } from "@/components/ui-kit";
import VehicleSearchSelect from "@/components/VehicleSearchSelect";
import { Plus, Trash, MagnifyingGlass, DownloadSimple, UploadSimple, Image as ImageIcon, ArrowUpRight, Info, Printer } from "@phosphor-icons/react";
import { formatDate, formatDateTime } from "@/lib/format";
import { downloadCsv, printSections } from "@/lib/csv";
import ReportToolbar, { ZoomScope, ZOOM_DEFAULT } from "@/components/ReportToolbar";
import GrnDetailsModal from "@/components/GrnDetailsModal";
import {
    TAXONOMY, BRAND_SUGGESTIONS, SKU_HELP, SKU_FORMAT_NOTE,
    sanitizeSkuToken, buildSku, isValidSku, getSubsForMain,
} from "@/lib/skuTaxonomy";

const empty = {
    sku: "", name: "",
    category: "", main_category: "", sub_category: "",
    brand_code: "", part_code: "",
    sku_mode: "auto", // "auto" | "manual"
    unit: "pcs", unit_cost: 0, quantity: 0, min_stock: 0, max_stock: 0,
    vendor_id: "", location_id: "",
};

export default function Inventory() {
    const [list, setList] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [locations, setLocations] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(empty);
    const [editing, setEditing] = useState(null);
    const [tab, setTab] = useState("items");
    const [grnOpen, setGrnOpen] = useState(false);
    const [grnLines, setGrnLines] = useState([{ inventory_id: "", quantity: 1, unit_cost: 0 }]);
    const [grnVendor, setGrnVendor] = useState("");

    // Issue stock modal state
    const [issueOpen, setIssueOpen] = useState(false);
    const [issueLines, setIssueLines] = useState([{ inventory_id: "", quantity: 1 }]);
    const [issueVehicle, setIssueVehicle] = useState("");
    const [issueNotes, setIssueNotes] = useState("");
    const [grns, setGrns] = useState([]);
    const [transactions, setTransactions] = useState([]);
    const [images, setImages] = useState([]); // images for the SKU currently being edited
    const [search, setSearch] = useState("");
    const [grnFrom, setGrnFrom] = useState("");
    const [grnTo, setGrnTo] = useState("");
    const [txFrom, setTxFrom] = useState("");
    const [txTo, setTxTo] = useState("");
    // Full inventory reference (all SKUs, unpaginated) used to resolve
    // item names/SKUs/units in GRN details, transactions tab, etc.
    const [invRefMap, setInvRefMap] = useState({});
    // GRN drilldown
    const [selectedGrn, setSelectedGrn] = useState(null);

    const inDateRange = (iso, from, to) => {
        if (!iso) return false;
        const d = new Date(iso);
        if (isNaN(d)) return false;
        if (from) {
            const f = new Date(from);
            if (d < f) return false;
        }
        if (to) {
            // include the entire 'to' day
            const t = new Date(to);
            t.setHours(23, 59, 59, 999);
            if (d > t) return false;
        }
        return true;
    };

    const filteredGrns = useMemo(() => {
        if (!grnFrom && !grnTo) return grns;
        return grns.filter((g) => inDateRange(g.created_at, grnFrom, grnTo));
    }, [grns, grnFrom, grnTo]);

    // SKUs that actually have stock — re-used by the Issue modal dropdown so we
    // don't recompute on every keystroke / line edit.
    const inStockItems = useMemo(
        () => list.filter((i) => (i.quantity || 0) > 0),
        [list]
    );

    const filteredTransactions = useMemo(() => {
        if (!txFrom && !txTo) return transactions;
        return transactions.filter((t) => inDateRange(t.created_at, txFrom, txTo));
    }, [transactions, txFrom, txTo]);

    const exportGrnsCsv = () => {
        downloadCsv(`grns-${new Date().toISOString().slice(0, 10)}.csv`, filteredGrns, [
            ["GRN #", "grn_number"],
            ["DATE", (g) => formatDate(g.created_at)],
            ["VENDOR", "vendor_id"],
            ["LINE COUNT", (g) => g.items?.length || 0],
            ["TOTAL COST (INR)", "total_cost"],
            ["NOTES", "notes"],
        ]);
    };

    const exportTransactionsCsv = () => {
        downloadCsv(`transactions-${new Date().toISOString().slice(0, 10)}.csv`, filteredTransactions, [
            ["DATE", (t) => formatDateTime(t.created_at)],
            ["TYPE", "type"],
            ["ITEM SKU", (t) => invMap[t.inventory_id]?.sku || ""],
            ["ITEM NAME", (t) => invMap[t.inventory_id]?.name || ""],
            ["QUANTITY", "quantity"],
            ["UNIT COST (INR)", "unit_cost"],
            ["WORK ORDER", "work_order_id"],
            ["VEHICLE", "vehicle_id"],
        ]);
    };

    const filtered = list; // server now applies the search filter; kept name for downstream usage

    const newLineKey = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `k-${Math.random().toString(36).slice(2)}`);

    // Pagination state — server returns latest first
    const PAGE_SIZE = 20;
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [busy, setBusy] = useState(false);
    const [zoom, setZoom] = useState(ZOOM_DEFAULT);

    const buildFilterParams = useCallback(() => {
        const params = new URLSearchParams();
        if (search.trim()) params.set("q", search.trim());
        return params;
    }, [search]);

    const load = useCallback(async (targetPage) => {
        const tp = targetPage ?? 1;
        setBusy(true);
        try {
            const params = buildFilterParams();
            params.set("page", String(tp));
            params.set("page_size", String(PAGE_SIZE));
            const r = await api.get(`/inventory?${params.toString()}`);
            const payload = r.data || {};
            setList(payload.items || []);
            setTotal(payload.total ?? 0);
            setPage(payload.page ?? tp);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Failed to load inventory");
        } finally {
            setBusy(false);
        }
    }, [buildFilterParams]);

    useEffect(() => {
        load(1);
        api.get("/vendors").then((r) => setVendors(r.data)).catch(() => {});
        api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {});
        api.get("/locations").then((r) => setLocations(r.data)).catch(() => {});
        api.get("/inventory/grns").then((r) => setGrns(r.data)).catch(() => {});
        api.get("/inventory/transactions").then((r) => setTransactions(r.data)).catch(() => {});
        // Full inventory list (no pagination) used solely as a lookup table
        // for SKU/name/unit in GRN details, transaction labels, etc.
        api.get("/inventory").then((r) => {
            const items = Array.isArray(r.data) ? r.data : (r.data?.items || []);
            setInvRefMap(Object.fromEntries(items.map((i) => [i.id, i])));
        }).catch(() => {});
    }, [load]);

    // Refetch on search change → reset to page 1
    useEffect(() => {
        setPage(1);
        load(1);
    }, [search, load]);

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    const exportCsv = async () => {
        const rows = await fetchAllFiltered();
        downloadCsv(`inventory-${new Date().toISOString().slice(0, 10)}.csv`, rows, [
            ["SKU", "sku"],
            ["NAME", "name"],
            ["CATEGORY", "category"],
            ["UNIT", "unit"],
            ["QUANTITY", "quantity"],
            ["MIN STOCK", "min_stock"],
            ["MAX STOCK", "max_stock"],
            ["UNIT COST (INR)", "unit_cost"],
            ["TOTAL VALUE (INR)", (i) => (i.quantity || 0) * (i.unit_cost || 0)],
            ["LOW STOCK", (i) => ((i.quantity || 0) <= (i.min_stock || 0) ? "YES" : "NO")],
        ]);
    };
    const fetchAllFiltered = async () => {
        const params = buildFilterParams();
        const r = await api.get(`/inventory${params.toString() ? `?${params.toString()}` : ""}`);
        return Array.isArray(r.data) ? r.data : (r.data?.items || []);
    };
    const exportPdf = async () => {
        const rows = await fetchAllFiltered();
        printSections(`EAZYFLEET · Inventory · ${rows.length} SKU(s)`, [{
            title: "Inventory",
            rows,
            columns: [
                ["SKU", "sku"],
                ["NAME", "name"],
                ["CATEGORY", "category"],
                ["QTY", (i) => `${i.quantity || 0} ${i.unit || ""}`],
                ["MIN", "min_stock"],
                ["UNIT COST (INR)", (i) => `₹${(i.unit_cost || 0).toLocaleString("en-IN")}`],
                ["TOTAL VALUE (INR)", (i) => `₹${((i.quantity || 0) * (i.unit_cost || 0)).toLocaleString("en-IN")}`],
                ["STATUS", (i) => ((i.quantity || 0) <= (i.min_stock || 0) ? "LOW" : "OK")],
            ],
        }]);
    };

    const loadImages = async (iid) => {
        try {
            const r = await api.get(`/inventory/${iid}/images`);
            setImages(r.data);
        } catch { setImages([]); }
    };

    const uploadImages = async (files) => {
        if (!editing) { toast.error("Save the SKU first, then add photos"); return; }
        const tid = toast.loading(`Uploading ${files.length} photo(s)…`);
        try {
            for (const f of files) {
                const fd = new FormData(); fd.append("file", f);
                await api.post(`/upload?entity_type=inventory&entity_id=${editing}&label=photo`, fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
            }
            toast.success("Photos uploaded", { id: tid });
            loadImages(editing);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail), { id: tid }); }
    };

    const removeImage = async (fid) => {
        if (!window.confirm("Remove photo?")) return;
        await api.delete(`/files/${fid}`);
        loadImages(editing);
    };

    const save = async () => {
        // Build/validate SKU before POST. In auto mode we compose from segments; in manual mode the user can type anything.
        let sku = form.sku;
        if (form.sku_mode === "auto") {
            sku = buildSku({
                main: form.main_category,
                sub: form.sub_category,
                brand: form.brand_code,
                part: form.part_code,
            });
        }
        if (!sku) { toast.error("SKU is required"); return; }
        if (form.sku_mode === "auto" && !isValidSku(sku)) {
            toast.error("SKU must be 12 chars (XX-XX-XXXX-XXXX). Fill all 4 segments.");
            return;
        }
        if (!form.name?.trim()) { toast.error("Name is required"); return; }

        // Auto-fill category label from the chosen sub-category so old screens stay backward compatible.
        const subLabel = form.sub_category
            ? (TAXONOMY.find((m) => m.code === form.main_category)?.subs.find((s) => s.code === form.sub_category)?.name || form.sub_category)
            : form.category;

        try {
            const payload = {
                ...form,
                sku,
                category: subLabel || form.category || "Uncategorised",
                unit_cost: Number(form.unit_cost),
                quantity: Number(form.quantity),
                min_stock: Number(form.min_stock),
                max_stock: Number(form.max_stock),
                vendor_id: form.vendor_id || null,
                location_id: form.location_id || null,
            };
            // Drop UI-only field
            delete payload.sku_mode;
            if (editing) await api.put(`/inventory/${editing}`, payload);
            else await api.post("/inventory", payload);
            toast.success("Saved");
            setOpen(false); setEditing(null); setForm(empty); load(page);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    const edit = (it) => {
        // Best-effort: parse an existing structured SKU back into the segments so editing keeps the same form.
        const parts = (it.sku || "").split("-");
        const parsed = parts.length === 4
            ? { main_category: parts[0], sub_category: `${parts[0]}-${parts[1]}`, brand_code: parts[2], part_code: parts[3], sku_mode: "auto" }
            : { sku_mode: "manual" };
        setForm({ ...empty, ...parsed, ...it, vendor_id: it.vendor_id || "", location_id: it.location_id || "" });
        setEditing(it.id);
        setOpen(true);
        loadImages(it.id);
    };
    const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/inventory/${id}`); load(page); };

    const submitGrn = async () => {
        try {
            const items = grnLines.filter((l) => l.inventory_id && l.quantity > 0).map((l) => ({ ...l, quantity: Number(l.quantity), unit_cost: Number(l.unit_cost) || 0 }));
            if (items.length === 0) { toast.error("Add at least one line"); return; }
            await api.post("/inventory/grn", { vendor_id: grnVendor || null, items });
            toast.success("GRN posted");
            setGrnOpen(false); setGrnLines([{ inventory_id: "", quantity: 1, unit_cost: 0 }]); setGrnVendor("");
            load(page);
            api.get("/inventory/grns").then((r) => setGrns(r.data));
            api.get("/inventory/transactions").then((r) => setTransactions(r.data));
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    const openIssueModal = (prefilledItem = null) => {
        setIssueOpen(true);
        setIssueLines(prefilledItem
            ? [{ inventory_id: prefilledItem.id, quantity: 1 }]
            : [{ inventory_id: "", quantity: 1 }]);
        setIssueVehicle("");
        setIssueNotes("");
    };

    const submitIssue = async () => {
        try {
            const items = issueLines
                .filter((l) => l.inventory_id && l.quantity > 0)
                .map((l) => ({ inventory_id: l.inventory_id, quantity: Number(l.quantity) }));
            if (items.length === 0) { toast.error("Add at least one line"); return; }
            // Client-side stock check so the user sees which SKU is short.
            for (const l of items) {
                const inv = invMap[l.inventory_id];
                if (inv && l.quantity > (inv.quantity || 0)) {
                    toast.error(`Not enough stock for ${inv.sku} (available ${inv.quantity} ${inv.unit})`);
                    return;
                }
            }
            const payload = { items, vehicle_id: issueVehicle || null, notes: issueNotes || null };
            const r = await api.post("/inventory/issue", payload);
            toast.success(`Issued ${items.length} line(s) · ₹${(r.data.total_cost || 0).toLocaleString("en-IN")}`);
            setIssueOpen(false);
            setIssueLines([{ inventory_id: "", quantity: 1 }]);
            setIssueVehicle("");
            setIssueNotes("");
            load(page);
            api.get("/inventory/transactions").then((r) => setTransactions(r.data));
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    const invMap = Object.fromEntries(list.map((i) => [i.id, i]));
    const vendorMap = Object.fromEntries((vendors || []).map((v) => [v.id, v]));

    return (
        <div className="p-6 lg:p-8 animate-fade-in">
            <PageHeader
                eyebrow="STORES & SPARES"
                title="Inventory"
                subtitle={`${list.length} SKUs tracked`}
                action={
                    <div className="flex gap-2">
                        <Button variant="secondary" onClick={() => openIssueModal()} data-testid="new-issue-btn"><ArrowUpRight size={14} /> Issue stock</Button>
                        <Button variant="secondary" onClick={() => setGrnOpen(true)} data-testid="new-grn-btn"><Plus size={14} /> New GRN</Button>
                        <NewButton onClick={() => { setForm(empty); setEditing(null); setOpen(true); }} label="Add SKU" testid="add-sku-btn" />
                    </div>
                }
            />

            <div className="flex gap-1 border-b border-fleet-border mb-6">
                {[["items", "Items"], ["grns", "GRNs"], ["transactions", "Transactions"]].map(([k, l]) => (
                    <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
                        className={`px-4 py-2 text-sm border-b-2 -mb-px transition-colors ${tab === k ? "border-fleet-ink text-fleet-ink font-medium" : "border-transparent text-fleet-inkSoft hover:text-fleet-ink"}`}>
                        {l}
                    </button>
                ))}
            </div>

            {tab === "items" && (
                <Surface className="overflow-hidden">
                    <div className="px-5 py-3 border-b border-fleet-border flex items-center gap-3">
                        <div className="relative flex-1 max-w-md">
                            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-fleet-inkMuted" />
                            <input
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search by SKU, name or category…"
                                className="w-full pl-9 pr-3 py-2 text-sm border border-fleet-border rounded-md focus:outline-none focus:border-fleet-ink"
                                data-testid="inventory-search-input"
                            />
                        </div>
                    </div>
                    <div className="px-5 py-2 border-b border-fleet-border">
                        <ReportToolbar
                            page={page} totalPages={totalPages} total={total} pageSize={PAGE_SIZE}
                            onPageChange={(p) => { setPage(p); load(p); }}
                            onRefresh={() => load(page)}
                            zoom={zoom} onZoomChange={setZoom}
                            onExportCsv={exportCsv}
                            onExportPdf={exportPdf}
                            busy={busy}
                        />
                    </div>
                    <ZoomScope zoom={zoom}>
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50">
                            <tr className="text-left">
                                {["SKU", "NAME", "CATEGORY", "QTY", "MIN", "UNIT COST", "STATUS", ""].map((h) => <th key={h} className="label-mono px-5 py-3 font-normal">{h}</th>)}
                            </tr>
                        </thead>
                        <tbody>
                            {list.map((i) => {
                                const low = (i.quantity || 0) <= (i.min_stock || 0);
                                return (
                                    <tr key={i.id} className="border-t border-fleet-border data-grid-row" data-testid={`inv-row-${i.id}`}>
                                        <td className="px-5 py-3 font-mono">{i.sku}</td>
                                        <td className="px-5 py-3 font-medium">{i.name}</td>
                                        <td className="px-5 py-3">{i.category}</td>
                                        <td className="px-5 py-3 font-mono">{i.quantity} {i.unit}</td>
                                        <td className="px-5 py-3 font-mono text-fleet-inkSoft">{i.min_stock}</td>
                                        <td className="px-5 py-3 font-mono">₹{(i.unit_cost || 0).toLocaleString("en-IN")}</td>
                                        <td className="px-5 py-3">{low ? <Badge variant="critical">LOW</Badge> : <Badge variant="healthy">OK</Badge>}</td>
                                        <td className="px-5 py-3 text-right">
                                            <div className="inline-flex">
                                                {(i.quantity || 0) > 0 && (
                                                    <Button variant="ghost" size="sm" onClick={() => openIssueModal(i)} data-testid={`issue-${i.id}`}>
                                                        <ArrowUpRight size={14} /> Issue
                                                    </Button>
                                                )}
                                                <Button variant="ghost" size="sm" onClick={() => edit(i)} data-testid={`edit-inv-${i.id}`}>Edit</Button>
                                                <Button variant="ghost" size="sm" onClick={() => del(i.id)} data-testid={`del-inv-${i.id}`}>Delete</Button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                            {list.length === 0 && (
                                <tr><td colSpan={8} className="px-5 py-10 text-center text-sm text-fleet-inkSoft">
                                    {total === 0 ? "No items yet. Add your first SKU." : "No SKUs match your search."}
                                </td></tr>
                            )}
                        </tbody>
                    </table>
                    </ZoomScope>
                </Surface>
            )}

            {tab === "grns" && (
                <Surface className="overflow-hidden">
                    <div className="px-5 py-3 border-b border-fleet-border flex items-center gap-3 flex-wrap">
                        <span className="label-mono">DATE RANGE</span>
                        <input type="date" value={grnFrom} onChange={(e) => setGrnFrom(e.target.value)}
                            className="px-2 py-1.5 text-sm border border-fleet-border rounded-md focus:outline-none focus:border-fleet-ink"
                            data-testid="grn-from-date" />
                        <span className="text-xs text-fleet-inkSoft">to</span>
                        <input type="date" value={grnTo} onChange={(e) => setGrnTo(e.target.value)}
                            className="px-2 py-1.5 text-sm border border-fleet-border rounded-md focus:outline-none focus:border-fleet-ink"
                            data-testid="grn-to-date" />
                        {(grnFrom || grnTo) && (
                            <button onClick={() => { setGrnFrom(""); setGrnTo(""); }} className="text-xs text-fleet-inkSoft hover:text-fleet-ink underline">
                                Clear
                            </button>
                        )}
                        <span className="text-xs text-fleet-inkSoft ml-auto">{filteredGrns.length} of {grns.length}</span>
                        <Button variant="secondary" size="sm" onClick={exportGrnsCsv} data-testid="grn-export-csv">
                            <DownloadSimple size={14} /> Export CSV
                        </Button>
                    </div>
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50"><tr className="text-left">
                            {["GRN #", "DATE", "ITEMS", "TOTAL"].map((h) => <th key={h} className="label-mono px-5 py-3 font-normal">{h}</th>)}
                        </tr></thead>
                        <tbody>
                            {filteredGrns.map((g) => (
                                <tr key={g.id} className="border-t border-fleet-border hover:bg-slate-50 cursor-pointer"
                                    onClick={() => setSelectedGrn(g)}
                                    data-testid={`grn-row-${g.grn_number}`}
                                    title="Click to view GRN details and purchase history">
                                    <td className="px-5 py-3 font-mono text-fleet-ink underline-offset-2 hover:underline">{g.grn_number}</td>
                                    <td className="px-5 py-3 font-mono">{formatDate(g.created_at)}</td>
                                    <td className="px-5 py-3 font-mono">{g.items?.length || 0}</td>
                                    <td className="px-5 py-3 font-mono">₹{(g.total_cost || 0).toLocaleString("en-IN")}</td>
                                </tr>
                            ))}
                            {filteredGrns.length === 0 && <tr><td colSpan={4} className="px-5 py-10 text-center text-sm text-fleet-inkSoft">{grns.length === 0 ? "No GRNs yet." : "No GRNs in this date range."}</td></tr>}
                        </tbody>
                    </table>
                </Surface>
            )}

            {tab === "transactions" && (
                <Surface className="overflow-hidden">
                    <div className="px-5 py-3 border-b border-fleet-border flex items-center gap-3 flex-wrap">
                        <span className="label-mono">DATE RANGE</span>
                        <input type="date" value={txFrom} onChange={(e) => setTxFrom(e.target.value)}
                            className="px-2 py-1.5 text-sm border border-fleet-border rounded-md focus:outline-none focus:border-fleet-ink"
                            data-testid="tx-from-date" />
                        <span className="text-xs text-fleet-inkSoft">to</span>
                        <input type="date" value={txTo} onChange={(e) => setTxTo(e.target.value)}
                            className="px-2 py-1.5 text-sm border border-fleet-border rounded-md focus:outline-none focus:border-fleet-ink"
                            data-testid="tx-to-date" />
                        {(txFrom || txTo) && (
                            <button onClick={() => { setTxFrom(""); setTxTo(""); }} className="text-xs text-fleet-inkSoft hover:text-fleet-ink underline">
                                Clear
                            </button>
                        )}
                        <span className="text-xs text-fleet-inkSoft ml-auto">{filteredTransactions.length} of {transactions.length}</span>
                        <Button variant="secondary" size="sm" onClick={exportTransactionsCsv} data-testid="tx-export-csv">
                            <DownloadSimple size={14} /> Export CSV
                        </Button>
                    </div>
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50"><tr className="text-left">
                            {["DATE", "TYPE", "ITEM", "QTY", "UNIT COST"].map((h) => <th key={h} className="label-mono px-5 py-3 font-normal">{h}</th>)}
                        </tr></thead>
                        <tbody>
                            {filteredTransactions.map((t) => (
                                <tr key={t.id} className="border-t border-fleet-border">
                                    <td className="px-5 py-3 font-mono">{formatDateTime(t.created_at)}</td>
                                    <td className="px-5 py-3"><Badge variant={t.type === "grn" ? "healthy" : "warning"}>{t.type}</Badge></td>
                                    <td className="px-5 py-3">{invMap[t.inventory_id]?.name || t.inventory_id?.slice(0, 8)}</td>
                                    <td className="px-5 py-3 font-mono">{t.quantity > 0 ? `+${t.quantity}` : t.quantity}</td>
                                    <td className="px-5 py-3 font-mono">₹{(t.unit_cost || 0).toLocaleString("en-IN")}</td>
                                </tr>
                            ))}
                            {filteredTransactions.length === 0 && <tr><td colSpan={5} className="px-5 py-10 text-center text-sm text-fleet-inkSoft">{transactions.length === 0 ? "No transactions yet." : "No transactions in this date range."}</td></tr>}
                        </tbody>
                    </table>
                </Surface>
            )}

            {/* SKU modal */}
            <Modal open={open} onClose={() => { setOpen(false); setEditing(null); setImages([]); }} title={editing ? "Edit SKU" : "Add SKU"} size="lg"
                footer={<><Button variant="secondary" onClick={() => { setOpen(false); setEditing(null); setImages([]); }}>Cancel</Button><Button onClick={save} data-testid="save-sku">Save</Button></>}>
                {/* SKU builder card */}
                <div className="border border-fleet-border rounded-md p-4 mb-4 bg-slate-50/40">
                    <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                        <div className="flex items-center gap-2">
                            <span className="label-mono">SKU BUILDER</span>
                            <span
                                className="inline-flex items-center justify-center w-4 h-4 rounded-full text-fleet-inkSoft hover:text-fleet-ink cursor-help"
                                title={SKU_HELP}
                                data-testid="sku-help"
                            >
                                <Info size={14} />
                            </span>
                            <span className="text-xs text-fleet-inkSoft font-mono" title={SKU_HELP}>{SKU_FORMAT_NOTE}</span>
                        </div>
                        <div className="inline-flex rounded-md border border-fleet-border bg-white text-xs overflow-hidden" data-testid="sku-mode">
                            <button
                                onClick={() => setForm({ ...form, sku_mode: "auto" })}
                                className={`px-2.5 py-1 ${form.sku_mode === "auto" ? "bg-fleet-ink text-white" : "text-fleet-inkSoft hover:text-fleet-ink"}`}
                                data-testid="sku-mode-auto"
                            >Auto-generate</button>
                            <button
                                onClick={() => setForm({ ...form, sku_mode: "manual" })}
                                className={`px-2.5 py-1 ${form.sku_mode === "manual" ? "bg-fleet-ink text-white" : "text-fleet-inkSoft hover:text-fleet-ink"}`}
                                data-testid="sku-mode-manual"
                            >Manual</button>
                        </div>
                    </div>

                    {form.sku_mode === "auto" ? (
                        <>
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                                <Select
                                    label="Main category (XX)"
                                    value={form.main_category}
                                    onChange={(e) => setForm({ ...form, main_category: e.target.value, sub_category: "" })}
                                    options={[{ value: "", label: "Select…" },
                                        ...TAXONOMY.map((m) => ({ value: m.code, label: `${m.code} · ${m.name}` }))]}
                                    data-testid="sku-main"
                                />
                                <Select
                                    label="Sub-category (XX)"
                                    value={form.sub_category}
                                    onChange={(e) => setForm({ ...form, sub_category: e.target.value })}
                                    options={[{ value: "", label: form.main_category ? "Select…" : "Pick main first" },
                                        ...getSubsForMain(form.main_category).map((s) => ({ value: s.code, label: `${s.code} · ${s.name}` }))]}
                                    disabled={!form.main_category}
                                    data-testid="sku-sub"
                                />
                                <div>
                                    <label className="label-mono block mb-1.5">Brand code (XXXX)</label>
                                    <input
                                        type="text"
                                        value={form.brand_code}
                                        onChange={(e) => setForm({ ...form, brand_code: sanitizeSkuToken(e.target.value, 4) })}
                                        placeholder="BOSC, NGKP…"
                                        maxLength={4}
                                        list="brand-codes"
                                        className="w-full px-3 py-2 bg-white border border-fleet-border rounded-md text-sm font-mono uppercase focus:outline-none focus:border-fleet-ink"
                                        data-testid="sku-brand"
                                        title="4-char brand code · A–Z (no O/I) + 0–9"
                                    />
                                    <datalist id="brand-codes">
                                        {BRAND_SUGGESTIONS.map((b) => (
                                            <option key={b.code} value={b.code}>{b.name}</option>
                                        ))}
                                    </datalist>
                                    <div className="text-[10px] text-fleet-inkMuted mt-0.5">No O/I — confuses with 0/1</div>
                                </div>
                                <div>
                                    <label className="label-mono block mb-1.5">Part code (XXXX)</label>
                                    <input
                                        type="text"
                                        value={form.part_code}
                                        onChange={(e) => setForm({ ...form, part_code: sanitizeSkuToken(e.target.value, 4) })}
                                        placeholder="1001, 5W30…"
                                        maxLength={4}
                                        className="w-full px-3 py-2 bg-white border border-fleet-border rounded-md text-sm font-mono uppercase focus:outline-none focus:border-fleet-ink"
                                        data-testid="sku-part"
                                        title="4-char part identifier · A–Z (no O/I) + 0–9"
                                    />
                                </div>
                            </div>
                            <div className="mt-3 flex items-center gap-3 flex-wrap">
                                <span className="label-mono">PREVIEW</span>
                                <span
                                    className={`font-mono text-base font-semibold px-3 py-1 rounded-md border ${
                                        isValidSku(buildSku({ main: form.main_category, sub: form.sub_category, brand: form.brand_code, part: form.part_code }))
                                            ? "bg-emerald-50 border-emerald-200 text-emerald-900"
                                            : "bg-white border-fleet-border text-fleet-inkSoft"
                                    }`}
                                    data-testid="sku-preview"
                                >
                                    {buildSku({ main: form.main_category || "XX", sub: form.sub_category || "XX-XX", brand: form.brand_code || "XXXX", part: form.part_code || "XXXX" })}
                                </span>
                                {isValidSku(buildSku({ main: form.main_category, sub: form.sub_category, brand: form.brand_code, part: form.part_code }))
                                    ? <span className="text-xs text-emerald-700">✓ valid</span>
                                    : <span className="text-xs text-fleet-inkSoft">Fill all 4 segments to generate a valid SKU.</span>}
                            </div>
                        </>
                    ) : (
                        <div>
                            <Input
                                label="SKU (manual)"
                                value={form.sku}
                                onChange={(e) => setForm({ ...form, sku: e.target.value.toUpperCase() })}
                                placeholder="e.g. CUSTOM-PART-001"
                                data-testid="sku-manual-input"
                            />
                            <div className="text-xs text-fleet-inkSoft mt-1">
                                Manual mode for one-off items that don't fit the standard taxonomy.
                                Recommended format is still {SKU_FORMAT_NOTE}.
                            </div>
                        </div>
                    )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <Input label="Name" className="col-span-2" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. K&N Performance Oil Filter HP-1001" />
                    <Input label="Category label" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Auto-filled from sub-category — override if needed" />
                    <Input label="Unit" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="pcs, ltr, kg" />
                    <Input label="Quantity" type="number" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} />
                    <Input label="Unit cost (₹)" type="number" value={form.unit_cost} onChange={(e) => setForm({ ...form, unit_cost: e.target.value })} />
                    <Input label="Min stock" type="number" value={form.min_stock} onChange={(e) => setForm({ ...form, min_stock: e.target.value })} />
                    <Input label="Max stock" type="number" value={form.max_stock} onChange={(e) => setForm({ ...form, max_stock: e.target.value })} />
                    <Select label="Vendor" value={form.vendor_id} onChange={(e) => setForm({ ...form, vendor_id: e.target.value })}
                        options={[{ value: "", label: "— none —" }, ...vendors.map((v) => ({ value: v.id, label: v.name }))]} />
                    <Select label="Location" value={form.location_id} onChange={(e) => setForm({ ...form, location_id: e.target.value })}
                        options={[{ value: "", label: "— none —" }, ...locations.map((l) => ({ value: l.id, label: l.name }))]} />
                </div>

                {/* Photos */}
                <div className="mt-6 border-t border-fleet-border pt-5">
                    <div className="flex items-center justify-between mb-3">
                        <div>
                            <div className="label-mono">PHOTOS</div>
                            <div className="text-xs text-fleet-inkSoft mt-0.5">{editing ? "Add multiple item photos for identification & QC" : "Save the SKU first, then add photos"}</div>
                        </div>
                        {editing && (
                            <label className="cursor-pointer">
                                <input type="file" accept="image/*" multiple className="hidden"
                                    onChange={(e) => {
                                        const fs = Array.from(e.target.files || []);
                                        if (fs.length) uploadImages(fs);
                                        e.target.value = "";
                                    }}
                                    data-testid="sku-photos-input" />
                                <Button as="span" variant="secondary" size="sm"><UploadSimple size={14} /> Add photos</Button>
                            </label>
                        )}
                    </div>
                    {editing && images.length === 0 && (
                        <div className="border border-dashed border-fleet-border rounded-md p-6 text-center text-sm text-fleet-inkSoft flex flex-col items-center gap-2">
                            <ImageIcon size={22} className="text-fleet-inkMuted" />
                            No photos yet. Click "Add photos" to upload one or more images.
                        </div>
                    )}
                    {images.length > 0 && (
                        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-2.5">
                            {images.map((img) => (
                                <div key={img.id} className="relative group border border-fleet-border rounded-md overflow-hidden aspect-square bg-slate-50">
                                    <img src={fileUrl(img.id)}
                                        alt={img.original_filename}
                                        className="w-full h-full object-cover"
                                        onError={(e) => { e.target.style.display = "none"; }} />
                                    <button onClick={() => removeImage(img.id)}
                                        className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-white/90 border border-fleet-border flex items-center justify-center text-fleet-critical opacity-0 group-hover:opacity-100 transition-opacity"
                                        title="Remove photo">
                                        <Trash size={11} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </Modal>

            {/* GRN modal */}
            <Modal open={grnOpen} onClose={() => setGrnOpen(false)} title="New GRN — Goods Receipt Note" size="lg"
                footer={<><Button variant="secondary" onClick={() => setGrnOpen(false)}>Cancel</Button><Button onClick={submitGrn} data-testid="save-grn">Post GRN</Button></>}>
                <div className="space-y-4">
                    <Select label="Vendor" value={grnVendor} onChange={(e) => setGrnVendor(e.target.value)}
                        options={[{ value: "", label: "— none —" }, ...vendors.map((v) => ({ value: v.id, label: v.name }))]} />

                    <div className="border border-fleet-border rounded-md">
                        <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-slate-50 label-mono">
                            <div className="col-span-7">PART</div>
                            <div className="col-span-2">QTY</div>
                            <div className="col-span-2">UNIT COST</div>
                            <div className="col-span-1"></div>
                        </div>
                        {grnLines.map((line, idx) => (
                            <div key={line._key || idx} className="grid grid-cols-12 gap-2 px-3 py-2 border-t border-fleet-border">
                                <select className="col-span-7 px-2 py-1.5 border border-fleet-border rounded-md text-sm"
                                    value={line.inventory_id}
                                    onChange={(e) => setGrnLines((s) => s.map((l, i) => i === idx ? { ...l, inventory_id: e.target.value } : l))}>
                                    <option value="">Select part…</option>
                                    {list.map((i) => <option key={i.id} value={i.id}>{i.sku} · {i.name}</option>)}
                                </select>
                                <input type="number" className="col-span-2 px-2 py-1.5 border border-fleet-border rounded-md text-sm font-mono" value={line.quantity}
                                    onChange={(e) => setGrnLines((s) => s.map((l, i) => i === idx ? { ...l, quantity: e.target.value } : l))} />
                                <input type="number" className="col-span-2 px-2 py-1.5 border border-fleet-border rounded-md text-sm font-mono" value={line.unit_cost}
                                    onChange={(e) => setGrnLines((s) => s.map((l, i) => i === idx ? { ...l, unit_cost: e.target.value } : l))} />
                                <button onClick={() => setGrnLines((s) => s.filter((_, i) => i !== idx))} className="col-span-1 text-fleet-inkSoft hover:text-fleet-critical"><Trash size={16} /></button>
                            </div>
                        ))}
                        <button onClick={() => setGrnLines((s) => [...s, { _key: newLineKey(), inventory_id: "", quantity: 1, unit_cost: 0 }])}
                            className="px-3 py-2 text-sm text-fleet-inkSoft hover:text-fleet-ink border-t border-fleet-border w-full text-left flex items-center gap-1.5">
                            <Plus size={14} /> Add line
                        </button>
                    </div>
                </div>
            </Modal>

            {/* Issue Stock modal */}
            <Modal
                open={issueOpen}
                onClose={() => setIssueOpen(false)}
                title="Issue stock"
                size="lg"
                footer={<>
                    <Button variant="secondary" onClick={() => setIssueOpen(false)}>Cancel</Button>
                    <Button onClick={submitIssue} data-testid="save-issue">Issue</Button>
                </>}
            >
                <div className="space-y-4">
                    <div className="bg-slate-50 border border-fleet-border rounded-md p-3 text-xs text-fleet-inkSoft">
                        Issuing reduces stock and writes a transaction. Optionally tag a vehicle so the cost is mapped per asset.
                    </div>

                    <VehicleSearchSelect
                        vehicles={vehicles}
                        value={issueVehicle}
                        onChange={setIssueVehicle}
                        label="Vehicle (optional)"
                        placeholder="Search by reg # (or leave blank)…"
                        allowClear
                        testid="issue-vehicle-search"
                    />

                    <div className="border border-fleet-border rounded-md">
                        <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-slate-50 label-mono">
                            <div className="col-span-8">PART</div>
                            <div className="col-span-3">QTY</div>
                            <div className="col-span-1"></div>
                        </div>
                        {issueLines.map((line, idx) => {
                            const inv = invMap[line.inventory_id];
                            const available = inv ? (inv.quantity || 0) : 0;
                            const over = inv && Number(line.quantity) > available;
                            return (
                                <div key={line._key || idx} className="px-3 py-2 border-t border-fleet-border">
                                    <div className="grid grid-cols-12 gap-2 items-center">
                                        <select
                                            className="col-span-8 px-2 py-1.5 border border-fleet-border rounded-md text-sm"
                                            value={line.inventory_id}
                                            onChange={(e) => setIssueLines((s) => s.map((l, i) => i === idx ? { ...l, inventory_id: e.target.value } : l))}
                                            data-testid={`issue-line-${idx}-part`}
                                        >
                                            <option value="">Select part…</option>
                                            {inStockItems.map((i) => (
                                                <option key={i.id} value={i.id}>
                                                    {i.sku} · {i.name} (avail {i.quantity} {i.unit})
                                                </option>
                                            ))}
                                        </select>
                                        <input
                                            type="number" min="1"
                                            className={`col-span-3 px-2 py-1.5 border rounded-md text-sm font-mono ${over ? "border-fleet-critical" : "border-fleet-border"}`}
                                            value={line.quantity}
                                            onChange={(e) => setIssueLines((s) => s.map((l, i) => i === idx ? { ...l, quantity: e.target.value } : l))}
                                            data-testid={`issue-line-${idx}-qty`}
                                        />
                                        <button
                                            onClick={() => setIssueLines((s) => s.length === 1 ? s : s.filter((_, i) => i !== idx))}
                                            className="col-span-1 text-fleet-inkSoft hover:text-fleet-critical disabled:opacity-30"
                                            disabled={issueLines.length === 1}
                                            title="Remove line"
                                        >
                                            <Trash size={16} />
                                        </button>
                                    </div>
                                    {inv && (
                                        <div className={`mt-1 text-xs ${over ? "text-fleet-critical" : "text-fleet-inkSoft"}`}>
                                            {over
                                                ? `Only ${available} ${inv.unit} available · cannot issue ${line.quantity}`
                                                : `Available: ${available} ${inv.unit} · @ ₹${(inv.unit_cost || 0).toLocaleString("en-IN")}/${inv.unit}`}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                        <button
                            onClick={() => setIssueLines((s) => [...s, { _key: newLineKey(), inventory_id: "", quantity: 1 }])}
                            className="px-3 py-2 text-sm text-fleet-inkSoft hover:text-fleet-ink border-t border-fleet-border w-full text-left flex items-center gap-1.5"
                            data-testid="issue-add-line"
                        >
                            <Plus size={14} /> Add line
                        </button>
                    </div>

                    <Input
                        label="Notes (optional)"
                        value={issueNotes}
                        onChange={(e) => setIssueNotes(e.target.value)}
                        placeholder="e.g. WO-XXX, technician name, internal ref…"
                        data-testid="issue-notes"
                    />
                </div>
            </Modal>

            {/* GRN details drilldown — opens when a GRN row is clicked */}
            <GrnDetailsModal
                open={!!selectedGrn}
                onClose={() => setSelectedGrn(null)}
                grn={selectedGrn}
                invMap={{ ...invMap, ...invRefMap }}
                vendorMap={vendorMap}
                allGrns={grns}
            />
        </div>
    );
}
